import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Tooltip,
  Box,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { format } from "date-fns";
import apiUrl from "../api";
import ContractForm from "../contractForm/ContractForm";
import Select from "react-select";
import { io } from "socket.io-client";

const ContractTable = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
  const [contractToAction, setContractToAction] = useState(null);
  const [viewMode, setViewMode] = useState(null);
  const [properties, setProperties] = useState([]);
  const [socket, setSocket] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));

  // Filter states
  const [filters, setFilters] = useState({
    property: "",
    startDate: null,
    endDate: null,
    active: "",
  });

  // Dialogs
  const [openEditDialog, setOpenEditDialog] = useState(false);

  // Initialize Socket.IO connection
  useEffect(() => {
    const newSocket = io(apiUrl);
    setSocket(newSocket);

    // Clean up on unmount
    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Set up socket event listeners
  useEffect(() => {
    if (!socket) return;

    // Handle contract events
    socket.on("contract", handleContractEvent);

    return () => {
      socket.off("contract", handleContractEvent);
    };
  }, [socket, contracts, page, rowsPerPage, filters]);

  // Handle different contract events
  const handleContractEvent = (data) => {
    console.log("Received contract event:", data);

    // Only update if we're on the first page and not filtering
    // Otherwise just refetch the current page
    const isFirstPage = page === 0;
    const hasNoFilters =
      !filters.property &&
      !filters.startDate &&
      !filters.endDate &&
      filters.active === "";

    switch (data.action) {
      case "create":
        if (isFirstPage && hasNoFilters) {
          setContracts((prevContracts) => {
            // Add to the beginning of the array and maintain rowsPerPage limit
            const updatedContracts = [data.contract, ...prevContracts].slice(
              0,
              rowsPerPage
            );
            return updatedContracts;
          });
          setTotalItems((prevTotal) => prevTotal + 1);
        } else {
          fetchContracts();
        }
        break;

      case "update":
      case "reactivate":
      case "deactivate":
        // Check if the updated contract is in our current view
        const contractIndex = contracts.findIndex(
          (c) => c._id === data.contract._id
        );
        if (contractIndex !== -1) {
          setContracts((prevContracts) => {
            const updatedContracts = [...prevContracts];
            updatedContracts[contractIndex] = data.contract;
            return updatedContracts;
          });

          // If currently selected, update it too
          if (selectedContract && selectedContract._id === data.contract._id) {
            setSelectedContract(data.contract);
          }
        }
        break;

      case "delete":
        // Remove from the list if present
        setContracts((prevContracts) =>
          prevContracts.filter((c) => c._id !== data.contract._id)
        );
        setTotalItems((prevTotal) => Math.max(0, prevTotal - 1));

        // If it was being viewed or edited, close the dialog
        if (selectedContract && selectedContract._id === data.contract._id) {
          setOpenEditDialog(false);
          setSelectedContract(null);
        }
        break;

      default:
        console.log("Unknown contract action:", data.action);
    }
  };

  // Fetch properties for filter dropdown
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const token = user?.token;
        const response = await fetch(`${apiUrl}/property/getPropertiesNames`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          console.log(data);
          setProperties(data.propertiesNames);
        }
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    };

    fetchProperties();
  }, []);

  // Fetch contracts with pagination and filters
  const fetchContracts = async () => {
    setLoading(true);
    try {
      const token = user?.token;

      // Build query parameters
      let queryParams = new URLSearchParams({
        page: page + 1, // API is 1-indexed
        limit: rowsPerPage,
      });

      // Add filters if they exist
      if (filters.property) queryParams.append("property", filters.property);
      if (filters.startDate)
        queryParams.append(
          "startDate",
          format(filters.startDate, "yyyy-MM-dd")
        );
      if (filters.endDate)
        queryParams.append("endDate", format(filters.endDate, "yyyy-MM-dd"));
      if (filters.active !== "") queryParams.append("active", filters.active);

      const response = await fetch(
        `${apiUrl}/contract?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setContracts(data.contracts);
        setTotalItems(data.totalItems);
      } else {
        console.error("Failed to fetch contracts");
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch contracts on initial load and when pagination or filters change
  useEffect(() => {
    fetchContracts();
  }, [page, rowsPerPage, filters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
    setPage(0); // Reset to first page on filter change
  };

  const handleViewContract = (contract) => {
    setSelectedContract(contract);
    setViewMode("view");
    setOpenEditDialog(true);
  };

  const handleEditContract = (contract) => {
    setSelectedContract(contract);
    setViewMode("edit");
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (contract) => {
    setContractToAction(contract);
    setDeleteConfirmOpen(true);
  };

  const handleDeactivateClick = (contract) => {
    setContractToAction(contract);
    setDeactivateConfirmOpen(true);
  };

  const handleActivateClick = (contract) => {
    setContractToAction(contract);
    setActivateConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (contractToAction) {
      await handleDeleteContract(contractToAction._id);
      setDeleteConfirmOpen(false);
      setContractToAction(null);
    }
  };

  const handleConfirmDeactivate = async () => {
    if (contractToAction) {
      try {
        const token = user?.token;
        const response = await fetch(
          `${apiUrl}/contract/${contractToAction._id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ active: false }),
          }
        );

        if (response.ok) {
          // No need to refresh contracts manually, the socket will handle that
        } else {
          console.error("Failed to deactivate contract");
          // Fallback to manual refresh if socket doesn't update
          fetchContracts();
        }
      } catch (error) {
        console.error("Error deactivating contract:", error);
        fetchContracts();
      } finally {
        setDeactivateConfirmOpen(false);
        setContractToAction(null);
      }
    }
  };

  const handleConfirmActivate = async () => {
    if (contractToAction) {
      try {
        const token = user?.token;
        const response = await fetch(
          `${apiUrl}/contract/${contractToAction._id}/reactivate`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          // No need to refresh contracts manually, the socket will handle that
        } else {
          console.error("Failed to activate contract");
          // Fallback to manual refresh if socket doesn't update
          fetchContracts();
        }
      } catch (error) {
        console.error("Error activating contract:", error);
        fetchContracts();
      } finally {
        setActivateConfirmOpen(false);
        setContractToAction(null);
      }
    }
  };

  const handleDeleteContract = async (contractId) => {
    try {
      const token = user?.token;
      const response = await fetch(`${apiUrl}/contract/${contractId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // No need to refresh contracts manually, the socket will handle that
      } else {
        console.error("Failed to delete contract");
        // Fallback to manual refresh if socket doesn't update
        fetchContracts();
      }
    } catch (error) {
      console.error("Error deleting contract:", error);
      fetchContracts();
    }
  };

  const handleCloseConfirmDialog = () => {
    setDeleteConfirmOpen(false);
    setDeactivateConfirmOpen(false);
    setActivateConfirmOpen(false);
    setContractToAction(null);
  };

  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    setSelectedContract(null);
    setViewMode(null);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const clearFilters = () => {
    setFilters({
      property: "",
      startDate: null,
      endDate: null,
      active: "",
    });
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "56px",
      borderColor: "#c4c4c4",
      boxShadow: state.isFocused ? "0 0 0 1px #1976d2" : null,
      "&:hover": {
        borderColor: state.isFocused ? "#1976d2" : "#c4c4c4",
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 10,
      position: "absolute",
      width: "100%",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 10,
    }),
    // Adding additional style for dropdown container
    container: (base) => ({
      ...base,
      width: "100%",
      position: "relative",
    }),
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" component="div">
          Contracts
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={toggleFilters}
            sx={{ marginRight: 1 }}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </Box>
      </Box>

      {showFilters && (
        <Box sx={{ p: 2, bgcolor: "background.paper" }}>
          <Typography variant="subtitle2" gutterBottom>
            Filters
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <Select
                value={
                  properties.find((p) => p._id === filters.property)
                    ? {
                        value: filters.property,
                        label: properties.find(
                          (p) => p._id === filters.property
                        )?.name,
                      }
                    : null
                }
                onChange={(selectedOption) =>
                  handleFilterChange("property", selectedOption?.value || "")
                }
                options={properties.map((property) => ({
                  value: property._id,
                  label: property.name,
                }))}
                placeholder="Select Property"
                styles={selectStyles}
                isClearable
                isSearchable
              />
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date From"
                value={filters.startDate}
                onChange={(date) => handleFilterChange("startDate", date)}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date To"
                value={filters.endDate}
                onChange={(date) => handleFilterChange("endDate", date)}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>

            <FormControl sx={{ minWidth: 150 }}>
              <Select
                value={
                  filters.active === ""
                    ? { value: "", label: "Any" }
                    : filters.active === "true"
                    ? { value: "true", label: "Active" }
                    : filters.active === "false"
                    ? { value: "false", label: "Inactive" }
                    : null
                }
                onChange={(selectedOption) =>
                  handleFilterChange("active", selectedOption?.value || "")
                }
                options={[
                  { value: "", label: "Any" },
                  { value: "true", label: "Active" },
                  { value: "false", label: "Inactive" },
                ]}
                placeholder="Select Status"
                styles={selectStyles}
                menuPosition="fixed"
                menuPlacement="auto"
                menuPortalTarget={document.body}
                isClearable
              />
            </FormControl>

            <Button variant="outlined" onClick={clearFilters}>
              Clear Filters
            </Button>
          </Box>
        </Box>
      )}

      <TableContainer sx={{ maxHeight: "calc(100vh - 300px)" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Property</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Charge</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : contracts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No contracts found
                </TableCell>
              </TableRow>
            ) : (
              contracts.map((contract) => (
                <TableRow key={contract._id} hover>
                  <TableCell>{contract.title}</TableCell>
                  <TableCell>
                    {contract.property
                      ? properties.find((p) => p._id === contract.property._id)
                          ?.name
                      : "Unknown Property"}
                  </TableCell>
                  <TableCell>
                    {format(new Date(contract.start_date), "dd MMM yyyy")}
                  </TableCell>
                  <TableCell>
                    {format(new Date(contract.end_date), "dd MMM yyyy")}
                  </TableCell>
                  <TableCell>
                    €{contract.charge}
                    {" /month"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={contract.active ? "Active" : "Inactive"}
                      color={contract.active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton
                        onClick={() => handleViewContract(contract)}
                        size="small"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {user.permissions.canEditContract && (
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={() => handleEditContract(contract)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    )}
                    {user.permissions.canEditContract && (
                      <>
                        {contract.active ? (
                          <Tooltip title="Deactivate">
                            <IconButton
                              onClick={() => handleDeactivateClick(contract)}
                              size="small"
                              color="warning"
                            >
                              <BlockIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Activate">
                            <IconButton
                              onClick={() => handleActivateClick(contract)}
                              size="small"
                              color="success"
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {user.permissions.canDeleteContract && (
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => handleDeleteClick(contract)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Contract Form Dialog for Edit/View */}
      {selectedContract && (
        <ContractForm
          open={openEditDialog}
          handleClose={handleCloseDialog}
          contract={selectedContract}
          refreshContracts={fetchContracts}
          readOnly={viewMode === "view"}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the contract "
            {contractToAction?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={deactivateConfirmOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="deactivate-dialog-title"
        aria-describedby="deactivate-dialog-description"
      >
        <DialogTitle id="deactivate-dialog-title">
          Confirm Deactivation
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="deactivate-dialog-description">
            Are you sure you want to deactivate the contract "
            {contractToAction?.title}"? You can reactivate it later if needed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmDeactivate}
            color="warning"
            variant="contained"
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activation Confirmation Dialog */}
      <Dialog
        open={activateConfirmOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="activate-dialog-title"
        aria-describedby="activate-dialog-description"
      >
        <DialogTitle id="activate-dialog-title">Activate Contract</DialogTitle>
        <DialogContent>
          <DialogContentText id="activate-dialog-description">
            Are you sure you want to activate this contract?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
          <Button onClick={handleConfirmActivate} color="success" autoFocus>
            Activate
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ContractTable;
